import React, { useRef, useEffect } from "react";
import type { CVData, WorkEntry, EducationEntry } from "@/lib/cvParser";

// ── Editable text primitive ──────────────────────────────────────────────────
interface EditableTextProps {
  value: string;
  onUpdate: (v: string) => void;
  style?: React.CSSProperties;
  multiline?: boolean;
}

const EditableText = ({ value, onUpdate, style, multiline = false }: EditableTextProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value;
    }
  }, [value]);

  const handleBlur = () => {
    if (ref.current) {
      const v = ref.current.innerText.trim();
      if (v !== value) onUpdate(v);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{ outline: "none", cursor: "text", ...style }}
    >
      {value}
    </div>
  );
};

// ── Section heading ─────────────────────────────────────────────────────────
const SectionHeading = ({ children, onUpdate }: { children: string; onUpdate?: (v: string) => void }) => (
  <div style={{ marginBottom: "12px", marginTop: "16px" }}>
    {onUpdate ? (
      <EditableText
        value={children}
        onUpdate={onUpdate}
        style={{
          fontSize: "12px",
          fontWeight: 700,
          color: "#000",
          textTransform: "uppercase",
          letterSpacing: "1.5px",
          display: "inline-block",
        }}
      />
    ) : (
      <span style={{
        fontSize: "12px",
        fontWeight: 700,
        color: "#000",
        textTransform: "uppercase",
        letterSpacing: "1.5px",
        display: "inline-block",
      }}>{children}</span>
    )}
  </div>
);

// ── Divider ───────────────────────────────────────────────────────────────
const Divider = () => (
  <div style={{ height: "1px", backgroundColor: "#000", margin: "16px 0", opacity: 0.8 }} />
);

// ── Debounce helper ───────────────────────────────────────────────────────
function debounce<T extends (...args: any[]) => void>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}

interface SimpleTemplateProps {
  data: CVData;
  onUpdate?: (newData: CVData) => void;
}

const SimpleTemplate = React.forwardRef<HTMLDivElement, SimpleTemplateProps>(
  ({ data, onUpdate }, ref) => {

    const debouncedUpdate = React.useCallback(
      debounce((newData: CVData) => { if (onUpdate) onUpdate(newData); }, 300),
      [onUpdate]
    );

    const updH = (key: "name" | "jobTitle", val: string) => debouncedUpdate({ ...data, [key]: val });
    const updSection = (key: keyof NonNullable<CVData["sectionNames"]>, val: string) =>
      debouncedUpdate({ ...data, sectionNames: { ...(data.sectionNames || {}), [key]: val } as any });

    const updBasic = (i: number, val: string) => {
      const a = [...data.basicInfo]; a[i] = val; debouncedUpdate({ ...data, basicInfo: a });
    };
    const updProfile = (val: string) => debouncedUpdate({ ...data, profile: val });

    const updWork = (i: number, field: keyof WorkEntry, val: any) => {
      const a = [...data.workExperience]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updWorkBullet = (wi: number, bi: number, val: string) => {
      const a = [...data.workExperience];
      const b = [...a[wi].bullets]; b[bi] = val; a[wi] = { ...a[wi], bullets: b };
      debouncedUpdate({ ...data, workExperience: a });
    };
    const updEdu = (i: number, field: keyof EducationEntry, val: any) => {
      const a = [...data.education]; a[i] = { ...a[i], [field]: val };
      debouncedUpdate({ ...data, education: a });
    };
    const updSkill = (i: number, val: string) => {
      const a = [...data.keySkills]; a[i] = val; debouncedUpdate({ ...data, keySkills: a });
    };

    const sNames = data.sectionNames || {
      basicInfo: "Contact",
      profile: "Profile",
      keySkills: "Key Competencies",
      workExperience: "Professional Experience",
      education: "Education & Certifications",
      interests: "Extracurricular Activities",
      references: "References",
    };

    const baseFont = "Arial, Helvetica, sans-serif";

    return (
      <div
        ref={ref}
        id="cv-template-root"
        style={{
          backgroundColor: "#ffffff",
          color: "#000",
          fontFamily: baseFont,
          width: "794px",
          minHeight: "1122px",
          boxSizing: "border-box",
          padding: "48px 60px",
          margin: "0 auto",
        }}
      >
        {/* ── HEADER ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom: "20px" }}>
          <EditableText
            value={data.name}
            onUpdate={(v) => updH("name", v)}
            style={{ fontSize: "36px", fontWeight: 800, letterSpacing: "-0.5px", marginBottom: "8px" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", fontSize: "11px", color: "#333" }}>
            {data.basicInfo.map((item, i) => (
              <React.Fragment key={i}>
                <EditableText
                  value={item}
                  onUpdate={(v) => updBasic(i, v)}
                />
                {i < data.basicInfo.length - 1 && <span>•</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <Divider />

        {/* ── PROFILE ─────────────────────────────────────────────────── */}
        {data.profile && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("profile", v)}>{sNames.profile}</SectionHeading>
            <EditableText
              value={data.profile}
              onUpdate={updProfile}
              multiline
              style={{ fontSize: "12px", lineHeight: "1.6", color: "#333" }}
            />
            <Divider />
          </div>
        )}

        {/* ── SKILLS ──────────────────────────────────────────────────── */}
        {data.keySkills.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("keySkills", v)}>{sNames.keySkills}</SectionHeading>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px 16px" }}>
              {data.keySkills.map((skill, i) => (
                <EditableText
                  key={i}
                  value={skill}
                  onUpdate={(v) => updSkill(i, v)}
                  style={{ fontSize: "12px", color: "#333" }}
                />
              ))}
            </div>
            <Divider />
          </div>
        )}

        {/* ── WORK EXPERIENCE ─────────────────────────────────────────── */}
        {data.workExperience.length > 0 && (
          <div>
            <SectionHeading onUpdate={(v) => updSection("workExperience", v)}>{sNames.workExperience}</SectionHeading>
            {data.workExperience.map((entry, i) => (
              <div key={i} style={{ marginBottom: "16px" }} className="simple-work-entry">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
                  <EditableText
                    value={entry.company}
                    onUpdate={(v) => updWork(i, "company", v)}
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  />
                  <EditableText
                    value={entry.dates}
                    onUpdate={(v) => updWork(i, "dates", v)}
                    style={{ fontSize: "12px", fontWeight: 700 }}
                  />
                </div>
                <div style={{ marginBottom: "6px" }}>
                  <EditableText
                    value={entry.jobTitle}
                    onUpdate={(v) => updWork(i, "jobTitle", v)}
                    style={{ fontSize: "13px", fontWeight: 700 }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {entry.bullets.map((b, bi) => (
                    <div key={bi} style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                      <EditableText
                        value={b}
                        onUpdate={(v) => updWorkBullet(i, bi, v)}
                        multiline
                        style={{ fontSize: "12px", lineHeight: "1.5", color: "#333", flex: 1 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <Divider />
          </div>
        )}

        {/* ── BOTTOM TWO COLUMNS ─────────────────────────────────────── */}
        <div style={{ display: "flex", gap: "40px" }}>
          <div style={{ flex: 1 }}>
            <SectionHeading onUpdate={(v) => updSection("education", v)}>{sNames.education}</SectionHeading>
            {data.education.map((entry, i) => (
              <div key={i} style={{ marginBottom: "12px" }}>
                <EditableText
                  value={entry.qualification}
                  onUpdate={(v) => updEdu(i, "qualification", v)}
                  style={{ fontSize: "12px", fontWeight: 700, marginBottom: "2px" }}
                />
                <EditableText
                  value={entry.institution}
                  onUpdate={(v) => updEdu(i, "institution", v)}
                  style={{ fontSize: "12px", color: "#333", marginBottom: "2px" }}
                />
                <EditableText
                  value={entry.dates}
                  onUpdate={(v) => updEdu(i, "dates", v)}
                  style={{ fontSize: "12px", color: "#666" }}
                />
              </div>
            ))}
          </div>

          <div style={{ width: "1px", backgroundColor: "#000", opacity: 0.8 }} />

          <div style={{ flex: 1 }}>
            <SectionHeading onUpdate={(v) => updSection("interests", v)}>{sNames.interests}</SectionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {data.interests.map((item, i) => (
                <EditableText
                  key={i}
                  value={item}
                  onUpdate={(v) => {
                    const newArr = [...data.interests];
                    newArr[i] = v;
                    debouncedUpdate({ ...data, interests: newArr });
                  }}
                  multiline
                  style={{ fontSize: "12px", color: "#333" }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── REFERENCES ─────────────────────────────────────────────────── */}
        {data.references && (
          <div>
            <Divider />
            <SectionHeading onUpdate={(v) => updSection("references", v)}>{sNames.references}</SectionHeading>
            <EditableText
              value={data.references}
              onUpdate={(v) => debouncedUpdate({ ...data, references: v })}
              multiline
              style={{ fontSize: "12px", lineHeight: "1.6", color: "#333" }}
            />
          </div>
        )}

      </div>
    );
  }
);

SimpleTemplate.displayName = "SimpleTemplate";
(SimpleTemplate as any).templateName = "Simple";

export default SimpleTemplate;
